// src/components/admin/page-management/section-forms/ProductCatalogVerticalForm.js
import React from "react";
import { Info } from "lucide-react";
import ColorPicker from "../../shared/ColorPicker";
import { useEffect, useState } from "react";
import axios from "axios";
import { Settings,Grid,RefreshCw,MousePointer,CheckSquare,Type,Package,Shield,Eye,MessageCircle,DollarSign,ShoppingCart,Layers,TreePine,Folder,Filter,List,Layout,Image, Search } from "lucide-react";
import ImageUploader from "../ImageUploader";

const ProductCatalogVerticalForm = ({ content, updateContent }) => {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [availableParentCategories, setAvailableParentCategories] = useState(
    []
  );
  const [loadingParentCategories, setLoadingParentCategories] = useState(false);
  const [loadedParentCategories, setLoadedParentCategories] = useState([]);

  // useEffect to fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (content?.showAllCategories === false) {
        try {
          setLoadingCategories(true);
          const response = await axios.get("/api/categories/basic");
          setAvailableCategories(response.data);

          // Initialize selected categories if not already set
          if (!content.selectedCategoryIds) {
            const initialSelected = response.data.map((cat) => cat._id);
            updateContent("selectedCategoryIds", initialSelected);
          }
        } catch (error) {
          console.error("Error fetching categories:", error);
        } finally {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();
  }, [content?.showAllCategories]);

  //useEffect to update the category selection UI
  useEffect(() => {
    const container = document.getElementById("category-selection-container");
    if (
      container &&
      availableCategories.length > 0 &&
      content?.showAllCategories === false
    ) {
      container.innerHTML = "";

      availableCategories.forEach((category) => {
        const isSelected =
          content.selectedCategoryIds?.includes(category._id) || false;

        const div = document.createElement("div");
        div.className = "flex items-center mb-2";

        const input = document.createElement("input");
        input.type = "checkbox";
        input.className = "mr-2";
        input.checked = isSelected;
        input.onchange = () => {
          const currentSelected = [...(content.selectedCategoryIds || [])];
          if (input.checked && !currentSelected.includes(category._id)) {
            updateContent("selectedCategoryIds", [
              ...currentSelected,
              category._id,
            ]);
          } else if (!input.checked && currentSelected.includes(category._id)) {
            updateContent(
              "selectedCategoryIds",
              currentSelected.filter((id) => id !== category._id)
            );
          }
        };

        const label = document.createElement("label");
        label.className = "text-sm";
        label.textContent = category.name;

        div.appendChild(input);
        div.appendChild(label);
        container.appendChild(div);
      });
    }
  }, [
    availableCategories,
    content?.selectedCategoryIds,
    content?.showAllCategories,
  ]);

  useEffect(() => {
    const fetchParentCategories = async () => {
      if (content?.showParentCategories === true) {
        try {
          setLoadingParentCategories(true);
          const response = await axios.get("/api/parent-categories/basic");
          setAvailableParentCategories(response.data);
          setLoadedParentCategories(response.data);

          // Initialize selected parent categories if not already set
          if (
            !content.selectedParentCategoryIds ||
            content.selectedParentCategoryIds.length === 0
          ) {
            const initialSelected = response.data.map((parent) => parent._id);
            updateContent("selectedParentCategoryIds", initialSelected);
          }
        } catch (error) {
          console.error("Error fetching parent categories:", error);
        } finally {
          setLoadingParentCategories(false);
        }
      }
    };

    fetchParentCategories();
  }, [content?.showParentCategories]);

  // Helper for handling color changes to ensure proper format
  const handleColorChange = (key, value) => {
    // Ensure color values include the # if missing
    if (value && !value.startsWith("#") && value.length > 0) {
      value = "#" + value;
    }
    updateContent(key, value);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Categories Section</h3>
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-blue-100 p-2 rounded-lg mr-3">
      <Settings className="h-5 w-5 text-blue-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Basic Settings</h4>
      <p className="text-sm text-gray-500 mt-1">Configure the main section content and appearance</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Section Content */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Section Content</h5>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Section Title</label>
          <input
            type="text"
            value={content?.title || "Browse Categories"}
            onChange={(e) => updateContent("title", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Enter the main section heading"
          />
          <p className="text-xs text-gray-500">The main heading that appears at the top of your categories section</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            rows="3"
            value={
              content?.description ||
              "Browse our selection of premium packaging solutions"
            }
            onChange={(e) => updateContent("description", e.target.value)}
            placeholder="Brief description of the category section"
          />
          <p className="text-xs text-gray-500">Optional subtitle that provides more context about your categories</p>
        </div>
      </div>
    </div>

    {/* Appearance Settings */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Appearance</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Background Color</label>
        <div className="flex items-center space-x-3">
          <ColorPicker
            color={content?.backgroundColor || "#F9FAFB"}
            onChange={(color) => updateContent("backgroundColor", color)}
          />
          
        </div>
        <p className="text-xs text-gray-500">Background color for the entire categories section</p>
      </div>
    </div>

    {/* Search Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-green-100 p-2 rounded-lg mr-3">
          <Search className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Search Functionality</h5>
          <p className="text-sm text-gray-500">Allow users to search through categories</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="search-enabled"
              checked={content?.searchEnabled !== false}
              onChange={(e) => updateContent("searchEnabled", e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="search-enabled" className="text-sm font-medium text-gray-700">Enable Search</label>
            <p className="text-xs text-gray-500 mt-1">Add a search bar to help users find categories quickly</p>
          </div>
        </div>

        {content?.searchEnabled !== false && (
          <div className="pl-7 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Search Placeholder Text</label>
            <input
              type="text"
              value={content?.searchPlaceholder || "Search categories..."}
              onChange={(e) =>
                updateContent("searchPlaceholder", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter placeholder text for search box"
            />
            <p className="text-xs text-gray-500">Text that appears in the search box when it's empty</p>
          </div>
        )}
      </div>
    </div>
  </div>
</div>
     
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-purple-100 p-2 rounded-lg mr-3">
      <Layout className="h-5 w-5 text-purple-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Layout Settings</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how categories are displayed and styled</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Layout Behavior */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Layout Behavior</h5>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Slider Threshold</label>
          <div className="flex items-center space-x-3">
            <input
              type="number"
              min="1"
              max="20"
              value={content?.sliderThreshold || 9}
              onChange={(e) =>
                updateContent("sliderThreshold", parseInt(e.target.value))
              }
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <span className="text-sm text-gray-500">categories</span>
          </div>
          <p className="text-xs text-gray-500">
            Automatically switch to horizontal slider when there are more categories than this number
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category Image Shape</label>
          <select
            value={content?.categoryImageShape || "circle"}
            onChange={(e) =>
              updateContent("categoryImageShape", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="circle">Circular</option>
            <option value="square">Square with Rounded Corners</option>
          </select>
          <p className="text-xs text-gray-500">How category images should be displayed</p>
        </div>
      </div>
    </div>

    {/* Category Colors */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Category Colors</h5>
      <p className="text-sm text-gray-600 mb-4">Customize the appearance of category items in different states</p>
      
      {/* Normal State */}
      <div className="mb-6">
        <h6 className="font-medium text-gray-800 mb-3">Normal State</h6>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Border Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.categoryBorderColor || "#3b82f6"}
                onChange={(color) => updateContent("categoryBorderColor", color)}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryBorderColor || "#3b82f6" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Background Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.categoryBackgroundColor || "#f3f4f6"}
                onChange={(color) =>
                  updateContent("categoryBackgroundColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryBackgroundColor || "#f3f4f6" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Text Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.categoryTextColor || "#1f2937"}
                onChange={(color) => updateContent("categoryTextColor", color)}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryTextColor || "#1f2937" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Selected State */}
      <div className="mb-6">
        <h6 className="font-medium text-gray-800 mb-3">Selected State</h6>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Border Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.selectedCategoryBorderColor || "#3b82f6"}
                onChange={(color) =>
                  updateContent("selectedCategoryBorderColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.selectedCategoryBorderColor || "#3b82f6" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Background Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.selectedCategoryBackgroundColor || "#60a5fa"}
                onChange={(color) =>
                  updateContent("selectedCategoryBackgroundColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.selectedCategoryBackgroundColor || "#60a5fa" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Text Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.selectedCategoryTextColor || "#ffffff"}
                onChange={(color) =>
                  updateContent("selectedCategoryTextColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.selectedCategoryTextColor || "#ffffff" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hover State */}
      <div>
        <h6 className="font-medium text-gray-800 mb-3">Hover State</h6>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Background Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.categoryHoverBackgroundColor || "#e5e7eb"}
                onChange={(color) =>
                  updateContent("categoryHoverBackgroundColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryHoverBackgroundColor || "#e5e7eb" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Text Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.categoryHoverTextColor || "#111827"}
                onChange={(color) =>
                  updateContent("categoryHoverTextColor", color)
                }
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryHoverTextColor || "#111827" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Image Sizing */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-orange-100 p-2 rounded-lg mr-3">
          <Image className="h-4 w-4 text-orange-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Image Sizing</h5>
          <p className="text-sm text-gray-500">Control category image sizes for different layouts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Vertical Layout Image Size</label>
          <select
            value={content?.verticalImageSize || "medium"}
            onChange={(e) => updateContent("verticalImageSize", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
          <p className="text-xs text-gray-500">Size of category images when displayed in vertical layout</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Slider Layout Image Size</label>
          <select
            value={content?.sliderImageSize || "medium"}
            onChange={(e) => updateContent("sliderImageSize", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
          <p className="text-xs text-gray-500">Size of category images when displayed in horizontal slider</p>
        </div>
      </div>
    </div>
  </div>
</div>


<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 mb-6">
  <div className="flex items-center mb-6">
    <div className="bg-teal-100 p-2 rounded-lg mr-3">
      <Filter className="h-5 w-5 text-teal-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Category Selection</h4>
      <p className="text-sm text-gray-500 mt-1">Choose which categories to display in your section</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Display Options */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Display Options</h5>
      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5">
          <input
            type="checkbox"
            id="show-all-categories"
            checked={content?.showAllCategories !== false}
            onChange={(e) =>
              updateContent("showAllCategories", e.target.checked)
            }
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="show-all-categories" className="text-sm font-medium text-gray-700">Show All Categories</label>
          <p className="text-xs text-gray-500 mt-1">
            Display all available categories automatically. Uncheck to manually select specific categories.
          </p>
        </div>
      </div>
    </div>

    {/* Specific Category Selection */}
    {content?.showAllCategories === false && (
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <List className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h5 className="font-medium text-gray-900">Category Selection</h5>
            <p className="text-sm text-gray-500">Choose specific categories to display</p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Available Categories
          </label>
          
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-60 overflow-y-auto">
            {/* Loading State */}
            <div className="flex items-center justify-center py-4 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                <span>Loading categories...</span>
              </div>
            </div>
            
            {/* Category Container - This will be filled by the component's useEffect */}
            <div id="category-selection-container" className="space-y-2"></div>
            
            {/* Help Text */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">i</span>
                  </div>
                </div>
                <div className="text-xs text-blue-700">
                  <p className="font-medium mb-1">No categories showing?</p>
                  <p>This is normal for new sections. Save the page first, then edit again to see available categories.</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500">
            Select the categories you want to display. Unselected categories will be hidden from users.
          </p>
        </div>
      </div>
    )}

    {/* Selection Summary */}
    {content?.showAllCategories === false && (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h6 className="font-medium text-gray-900">Selection Summary</h6>
            <p className="text-sm text-gray-500">Categories will be filtered based on your selection</p>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-gray-900">
              {content?.selectedCategoryIds?.length || 0} Selected
            </div>
            <div className="text-xs text-gray-500">
              {content?.selectedCategoryIds?.length === 0 ? 'No categories selected' : 
               content?.selectedCategoryIds?.length === 1 ? '1 category selected' :
               `${content?.selectedCategoryIds?.length} categories selected`}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
</div>


      
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 mb-6">
  <div className="flex items-center mb-6">
    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
      <Layers className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Category Display Options</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how categories are organized and displayed</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Parent Categories Toggle */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Category Organization</h5>
      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5">
          <input
            type="checkbox"
            id="show-parent-categories"
            checked={content?.showParentCategories === true}
            onChange={(e) =>
              updateContent("showParentCategories", e.target.checked)
            }
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="show-parent-categories" className="text-sm font-medium text-gray-700">Show Parent Categories</label>
          <p className="text-xs text-gray-500 mt-1">
            Group related categories under parent headings with child categories displayed in organized rows
          </p>
        </div>
      </div>
    </div>

    {/* Parent Category Configuration */}
    {content?.showParentCategories === true && (
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <div className="bg-purple-100 p-2 rounded-lg mr-3">
            <TreePine className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <h5 className="font-medium text-gray-900">Parent Category Settings</h5>
            <p className="text-sm text-gray-500">Configure which parent categories to show</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Show All Parent Categories */}
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="show-all-parent-categories"
                checked={content?.showAllParentCategories !== false}
                onChange={(e) =>
                  updateContent("showAllParentCategories", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="show-all-parent-categories" className="text-sm font-medium text-gray-700">
                Show All Parent Categories
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Display all available parent categories automatically. Uncheck to manually select specific ones.
              </p>
            </div>
          </div>

          {/* Specific Parent Category Selection */}
          {content?.showAllParentCategories === false && (
            <div className="pl-7">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Parent Categories
                </label>
                
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-60 overflow-y-auto">
                  {loadingParentCategories ? (
                    <div className="flex items-center justify-center py-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                        <span>Loading parent categories...</span>
                      </div>
                    </div>
                  ) : loadedParentCategories.length > 0 ? (
                    <div className="space-y-2">
                      {loadedParentCategories.map((parentCategory) => (
                        <div
                          key={parentCategory._id}
                          className="flex items-center p-2 hover:bg-white rounded-lg transition-colors"
                        >
                          <input
                            type="checkbox"
                            id={`parent-${parentCategory._id}`}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-3"
                            checked={
                              content.selectedParentCategoryIds?.includes(
                                parentCategory._id
                              ) || false
                            }
                            onChange={(e) => {
                              const currentSelected = [
                                ...(content.selectedParentCategoryIds || []),
                              ];
                              if (
                                e.target.checked &&
                                !currentSelected.includes(parentCategory._id)
                              ) {
                                updateContent("selectedParentCategoryIds", [
                                  ...currentSelected,
                                  parentCategory._id,
                                ]);
                              } else if (
                                !e.target.checked &&
                                currentSelected.includes(parentCategory._id)
                              ) {
                                updateContent(
                                  "selectedParentCategoryIds",
                                  currentSelected.filter(
                                    (id) => id !== parentCategory._id
                                  )
                                );
                              }
                            }}
                          />
                          <label 
                            htmlFor={`parent-${parentCategory._id}`}
                            className="text-sm text-gray-700 cursor-pointer flex-1"
                          >
                            {parentCategory.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-sm text-gray-500 mb-2">No parent categories found</div>
                      <div className="text-xs text-gray-400">Try saving the page first and edit again</div>
                    </div>
                  )}
                </div>

                {/* Selection Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-blue-900">Selection Summary</div>
                      <div className="text-xs text-blue-700">
                        {content?.selectedParentCategoryIds?.length || 0} parent categories selected
                      </div>
                    </div>
                    <div className="text-blue-600">
                      <Folder className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Display Style Settings */}
          <div className="border-t border-gray-200 pt-6">
            <h6 className="font-medium text-gray-900 mb-4">Display Style</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Parent Category Style</label>
                <select
                  value={content?.parentCategoryDisplayStyle || "nameAndImage"}
                  onChange={(e) =>
                    updateContent("parentCategoryDisplayStyle", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="nameAndImage">Name with Image</option>
                  <option value="nameOnly">Name Only</option>
                </select>
                <p className="text-xs text-gray-500">How parent category headers should be displayed</p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Child Category Image Size</label>
                <select
                  value={content?.childCategoryImageSize || "small"}
                  onChange={(e) =>
                    updateContent("childCategoryImageSize", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="tiny">Tiny</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                </select>
                <p className="text-xs text-gray-500">Size of images for child categories under each parent</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Info Panel */}
    {content?.showParentCategories === true && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
          </div>
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Parent Category Mode</p>
            <p>Categories will be grouped under their parent categories with child categories displayed in organized rows beneath each parent.</p>
          </div>
        </div>
      </div>
    )}
  </div>
</div>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-emerald-100 p-2 rounded-lg mr-3">
      <DollarSign className="h-5 w-5 text-emerald-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Price Display Configuration</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how product prices are displayed and calculated</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Price Type Selection */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Price Type</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Display Price Type</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.priceDisplayType || "regular"}
          onChange={(e) => updateContent("priceDisplayType", e.target.value)}
        >
          <option value="regular">Regular Price</option>
          <option value="tier">Pricing Tier (Last Bound)</option>
          <option value="wholesale">Wholesale Price</option>
        </select>
        <p className="text-xs text-gray-500">
          Choose which pricing model to display when "Show Price" is enabled in your product settings
        </p>
      </div>

      {/* Price Type Info Panel */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">i</span>
            </div>
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {content?.priceDisplayType === "regular" && "Regular Pricing"}
              {content?.priceDisplayType === "tier" && "Tier Pricing"}
              {content?.priceDisplayType === "wholesale" && "Wholesale Pricing"}
            </p>
            <p>
              {content?.priceDisplayType === "regular" && "Shows the standard retail price for individual purchases."}
              {content?.priceDisplayType === "tier" && "Displays pricing based on quantity tiers, showing the last bound price."}
              {content?.priceDisplayType === "wholesale" && "Shows bulk pricing for business customers and larger orders."}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Quantity Selection Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-orange-100 p-2 rounded-lg mr-3">
          <ShoppingCart className="h-4 w-4 text-orange-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Cart Configuration</h5>
          <p className="text-sm text-gray-500">Control how customers can add products to their cart</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="allowQuantitySelection"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.allowQuantitySelection === true}
              onChange={(e) =>
                updateContent("allowQuantitySelection", e.target.checked)
              }
            />
          </div>
          <div className="flex-1">
            <label htmlFor="allowQuantitySelection" className="text-sm font-medium text-gray-700">
              Allow Quantity Selection
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Let customers choose how many items to add to cart before adding them
            </p>
          </div>
        </div>

        {content?.allowQuantitySelection && (
          <div className="pl-7 space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Default Quantity</label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  min="1"
                  max="1000"
                  value={
                    content?.defaultQuantity ||
                    (content?.priceDisplayType === "wholesale" ? 50 : 1)
                  }
                  onChange={(e) =>
                    updateContent(
                      "defaultQuantity",
                      parseInt(e.target.value, 10) ||
                        (content?.priceDisplayType === "wholesale" ? 50 : 1)
                    )
                  }
                />
                <span className="text-sm text-gray-500">items</span>
              </div>
              <p className="text-xs text-gray-500">
                The initial quantity that appears in the quantity selector
              </p>
            </div>

            {/* Quantity Behavior Info */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-700">
                <p className="font-medium mb-1">Quantity Behavior:</p>
                {content?.priceDisplayType === "regular" && (
                  <p>Regular pricing allows customers to select any quantity starting from the default value, incrementing by 1.</p>
                )}
                {content?.priceDisplayType === "tier" && (
                  <p>Tier pricing will automatically adjust quantities to match available pricing tiers and their bounds.</p>
                )}
                {content?.priceDisplayType === "wholesale" && (
                  <p>Wholesale pricing typically starts with higher default quantities suitable for bulk orders, incrementing by 1.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Summary Section */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Configuration Summary</h6>
          <p className="text-sm text-gray-500">Current pricing and cart settings</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {content?.priceDisplayType === "regular" && "Regular Pricing"}
            {content?.priceDisplayType === "tier" && "Tier Pricing"}
            {content?.priceDisplayType === "wholesale" && "Wholesale Pricing"}
          </div>
          <div className="text-xs text-gray-500">
            {content?.allowQuantitySelection ? 
              `Quantity selection enabled (default: ${content?.defaultQuantity || (content?.priceDisplayType === "wholesale" ? 50 : 1)})` : 
              'Fixed quantity (1 item per add)'
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

   
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-violet-100 p-2 rounded-lg mr-3">
      <Shield className="h-5 w-5 text-violet-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Price and Cart Visibility</h4>
      <p className="text-sm text-gray-500 mt-1">Control who can see prices and add products to cart</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Price Visibility Settings */}
    <div>
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 p-2 rounded-lg mr-3">
          <Eye className="h-4 w-4 text-blue-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Price Visibility</h5>
          <p className="text-sm text-gray-500">Choose who can see product prices</p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Show Price For</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.priceVisibility || "all"}
          onChange={(e) => updateContent("priceVisibility", e.target.value)}
        >
          <option value="all">Everyone (Default)</option>
          <option value="authenticated">Logged In Users Only</option>
          <option value="business">Business Users Only</option>
          <option value="regular">Regular Users Only</option>
          <option value="none">No One (Hide Prices)</option>
        </select>
        <p className="text-xs text-gray-500">
          Restrict price visibility based on user authentication and account type
        </p>
      </div>

      {/* Price visibility info panel */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">i</span>
            </div>
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {content?.priceVisibility === "all" && "Public Pricing"}
              {content?.priceVisibility === "authenticated" && "Login Required"}
              {content?.priceVisibility === "business" && "Business Only"}
              {content?.priceVisibility === "regular" && "Regular Users Only"}
              {content?.priceVisibility === "none" && "Prices Hidden"}
            </p>
            <p>
              {content?.priceVisibility === "all" && "All visitors can see product prices without any restrictions."}
              {content?.priceVisibility === "authenticated" && "Only users who have logged into their account can view prices."}
              {content?.priceVisibility === "business" && "Only users with business accounts can see pricing information."}
              {content?.priceVisibility === "regular" && "Only users with regular (non-business) accounts can view prices."}
              {content?.priceVisibility === "none" && "Product prices are completely hidden from all users."}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Cart Visibility Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-green-100 p-2 rounded-lg mr-3">
          <ShoppingCart className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Cart Access</h5>
          <p className="text-sm text-gray-500">Control who can add products to cart</p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Add to Cart For</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.cartVisibility || "all"}
          onChange={(e) => updateContent("cartVisibility", e.target.value)}
        >
          <option value="all">Everyone (Default)</option>
          <option value="authenticated">Logged In Users Only</option>
          <option value="business">Business Users Only</option>
          <option value="regular">Regular Users Only</option>
          <option value="none">No One (Disable Add to Cart)</option>
        </select>
        <p className="text-xs text-gray-500">
          Restrict cart functionality based on user authentication and account type
        </p>
      </div>

      {/* Cart visibility info panel */}
      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">i</span>
            </div>
          </div>
          <div className="text-sm text-green-800">
            <p className="font-medium mb-1">
              {content?.cartVisibility === "all" && "Open Cart Access"}
              {content?.cartVisibility === "authenticated" && "Login Required"}
              {content?.cartVisibility === "business" && "Business Only"}
              {content?.cartVisibility === "regular" && "Regular Users Only"}
              {content?.cartVisibility === "none" && "Cart Disabled"}
            </p>
            <p>
              {content?.cartVisibility === "all" && "All visitors can add products to cart and proceed with purchases."}
              {content?.cartVisibility === "authenticated" && "Only logged-in users can add products to cart."}
              {content?.cartVisibility === "business" && "Only business account holders can add products to cart."}
              {content?.cartVisibility === "regular" && "Only regular (non-business) users can add products to cart."}
              {content?.cartVisibility === "none" && "Add to cart functionality is completely disabled for all users."}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Login Prompt Message */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-amber-100 p-2 rounded-lg mr-3">
          <MessageCircle className="h-4 w-4 text-amber-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Access Prompt</h5>
          <p className="text-sm text-gray-500">Message shown to restricted users</p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Login Prompt Message</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder="Please login to see prices and purchase products"
          value={
            content?.loginPromptMessage ||
            "Please login to see prices and purchase products"
          }
          onChange={(e) =>
            updateContent("loginPromptMessage", e.target.value)
          }
        />
        <p className="text-xs text-gray-500">
          This message appears when users don't have permission to view prices or add items to cart
        </p>
      </div>

      {/* Message Preview */}
      {(content?.priceVisibility !== "all" || content?.cartVisibility !== "all") && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              <MessageCircle className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Message Preview</p>
              <p className="italic">
                "{content?.loginPromptMessage || "Please login to see prices and purchase products"}"
              </p>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Access Control Summary</h6>
          <p className="text-sm text-gray-500">Current visibility restrictions</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {(content?.priceVisibility === "all" && content?.cartVisibility === "all") ? 
              "Open Access" : "Restricted Access"}
          </div>
          <div className="text-xs text-gray-500">
            {content?.priceVisibility !== "all" && "Price restrictions active"}
            {content?.priceVisibility !== "all" && content?.cartVisibility !== "all" && " • "}
            {content?.cartVisibility !== "all" && "Cart restrictions active"}
            {content?.priceVisibility === "all" && content?.cartVisibility === "all" && "No restrictions applied"}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
     



// Enhanced Product Information Display Section - Direct Replacement
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-cyan-100 p-2 rounded-lg mr-3">
      <Package className="h-5 w-5 text-cyan-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Product Information Display</h4>
      <p className="text-sm text-gray-500 mt-1">Configure which product details to show and how they appear</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Information Fields */}
    <div>
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 p-2 rounded-lg mr-3">
          <CheckSquare className="h-4 w-4 text-blue-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Information Fields</h5>
          <p className="text-sm text-gray-500">Choose which product details to display</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="showName"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.productDisplayFields?.showName !== false}
              onChange={(e) =>
                updateContent("productDisplayFields", {
                  ...(content?.productDisplayFields || {}),
                  showName: e.target.checked,
                })
              }
            />
          </div>
          <div className="flex-1">
            <label htmlFor="showName" className="text-sm font-medium text-gray-700">
              Product Name
            </label>
            <p className="text-xs text-gray-500 mt-1">Display the product title/name</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="showPrice"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.productDisplayFields?.showPrice !== false}
              onChange={(e) =>
                updateContent("productDisplayFields", {
                  ...(content?.productDisplayFields || {}),
                  showPrice: e.target.checked,
                })
              }
            />
          </div>
          <div className="flex-1">
            <label htmlFor="showPrice" className="text-sm font-medium text-gray-700">
              Price Information
            </label>
            <p className="text-xs text-gray-500 mt-1">Show product pricing (subject to visibility rules)</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="showDescription"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.productDisplayFields?.showDescription === true}
              onChange={(e) =>
                updateContent("productDisplayFields", {
                  ...(content?.productDisplayFields || {}),
                  showDescription: e.target.checked,
                })
              }
            />
          </div>
          <div className="flex-1">
            <label htmlFor="showDescription" className="text-sm font-medium text-gray-700">
              Product Description
            </label>
            <p className="text-xs text-gray-500 mt-1">Display detailed product description</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="showNote"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.productDisplayFields?.showNote === true}
              onChange={(e) =>
                updateContent("productDisplayFields", {
                  ...(content?.productDisplayFields || {}),
                  showNote: e.target.checked,
                })
              }
            />
          </div>
          <div className="flex-1">
            <label htmlFor="showNote" className="text-sm font-medium text-gray-700">
              Product Note
            </label>
            <p className="text-xs text-gray-500 mt-1">Show additional notes or special information</p>
          </div>
        </div>
      </div>
    </div>

    {/* Layout Configuration */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-purple-100 p-2 rounded-lg mr-3">
          <Layout className="h-4 w-4 text-purple-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Layout Configuration</h5>
          <p className="text-sm text-gray-500">Control how product information is positioned and aligned</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Information Position</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.productInfoPosition || "below"}
            onChange={(e) =>
              updateContent("productInfoPosition", e.target.value)
            }
          >
            <option value="below">Below Image</option>
            <option value="right">Right of Image</option>
            <option value="left">Left of Image</option>
            <option value="overlay">Overlay on Image</option>
          </select>
          <p className="text-xs text-gray-500">Where to place product information relative to the image</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.productTextAlignment || "left"}
            onChange={(e) =>
              updateContent("productTextAlignment", e.target.value)
            }
          >
            <option value="left">Left Aligned</option>
            <option value="center">Center Aligned</option>
            <option value="right">Right Aligned</option>
          </select>
          <p className="text-xs text-gray-500">How to align text content within the information area</p>
        </div>
      </div>
    </div>

    {/* Text Display Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-green-100 p-2 rounded-lg mr-3">
          <Type className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Text Display Settings</h5>
          <p className="text-sm text-gray-500">Control text truncation and line limits</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Product Name Lines</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.productNameLines || 2}
            onChange={(e) =>
              updateContent("productNameLines", parseInt(e.target.value, 10))
            }
          >
            <option value={1}>1 Line</option>
            <option value={2}>2 Lines</option>
            <option value={3}>3 Lines</option>
            <option value={0}>No Limit</option>
          </select>
          <p className="text-xs text-gray-500">Maximum lines for product names (longer names will be truncated)</p>
        </div>

        {content?.productDisplayFields?.showDescription && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Description Lines</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={content?.productDescriptionLines || 3}
              onChange={(e) =>
                updateContent(
                  "productDescriptionLines",
                  parseInt(e.target.value, 10)
                )
              }
            >
              <option value={1}>1 Line</option>
              <option value={2}>2 Lines</option>
              <option value={3}>3 Lines</option>
              <option value={4}>4 Lines</option>
              <option value={5}>5 Lines</option>
              <option value={0}>No Limit</option>
            </select>
            <p className="text-xs text-gray-500">Maximum lines for product descriptions</p>
          </div>
        )}
      </div>

      {!content?.productDisplayFields?.showDescription && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Description line limit:</span> Enable "Product Description" above to configure description text limits.
          </p>
        </div>
      )}
    </div>

    {/* Image Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-orange-100 p-2 rounded-lg mr-3">
          <Image className="h-4 w-4 text-orange-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Image Display</h5>
          <p className="text-sm text-gray-500">Configure how product images are displayed</p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Image Aspect Ratio</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.productImageAspectRatio || "square"}
          onChange={(e) =>
            updateContent("productImageAspectRatio", e.target.value)
          }
        >
          <option value="square">Square (1:1)</option>
          <option value="portrait">Portrait (3:4)</option>
          <option value="landscape">Landscape (4:3)</option>
          <option value="original">Original (As uploaded)</option>
        </select>
        <p className="text-xs text-gray-500">How product images should be cropped and displayed</p>
      </div>

      {/* Aspect ratio preview */}
      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-700">Preview:</div>
          <div className="flex items-center space-x-2">
            <div 
              className="bg-gray-300 border border-gray-400 rounded"
              style={{
                width: content?.productImageAspectRatio === "landscape" ? "40px" : 
                       content?.productImageAspectRatio === "portrait" ? "24px" : "32px",
                height: content?.productImageAspectRatio === "landscape" ? "30px" : 
                        content?.productImageAspectRatio === "portrait" ? "32px" : "32px"
              }}
            />
            <span className="text-xs text-gray-500">
              {content?.productImageAspectRatio === "square" && "1:1 Square"}
              {content?.productImageAspectRatio === "portrait" && "3:4 Portrait"}
              {content?.productImageAspectRatio === "landscape" && "4:3 Landscape"}
              {content?.productImageAspectRatio === "original" && "Original Size"}
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Display Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Display Summary</h6>
          <p className="text-sm text-gray-500">Current product information settings</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {[
              content?.productDisplayFields?.showName !== false && "Name",
              content?.productDisplayFields?.showPrice !== false && "Price", 
              content?.productDisplayFields?.showDescription === true && "Description",
              content?.productDisplayFields?.showNote === true && "Note"
            ].filter(Boolean).length} Fields Enabled
          </div>
          <div className="text-xs text-gray-500">
            {content?.productInfoPosition || "below"} • {content?.productTextAlignment || "left"} aligned
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


{/* Product Interaction Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
      <MousePointer className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Product Click Behavior</h4>
      <p className="text-sm text-gray-500 mt-1">Configure what happens when users click on products</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Click Behavior Selection */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Click Action</h5>
      <div className="space-y-3">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="radio"
              id="productClickNavigate"
              name="productClickBehavior"
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
              checked={content?.productClickBehavior !== "popup"}
              onChange={() => updateContent("productClickBehavior", "navigate")}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="productClickNavigate" className="text-sm font-medium text-gray-700">
              Navigate to Product Page
            </label>
            <p className="text-xs text-gray-500 mt-1">Take users to the full product detail page</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="radio"
              id="productClickPopup"
              name="productClickBehavior"
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
              checked={content?.productClickBehavior === "popup"}
              onChange={() => updateContent("productClickBehavior", "popup")}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="productClickPopup" className="text-sm font-medium text-gray-700">
              Show Quick View Popup
            </label>
            <p className="text-xs text-gray-500 mt-1">Display product details in an overlay without leaving the page</p>
          </div>
        </div>
      </div>
    </div>

    {/* Popup Settings */}
    {content?.productClickBehavior === "popup" && (
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <div className="bg-purple-100 p-2 rounded-lg mr-3">
          </div>
          <div>
            <h5 className="font-medium text-gray-900">Popup Configuration</h5>
            <p className="text-sm text-gray-500">Customize the quick view popup content and appearance</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Information to Display */}
          <div>
            <h6 className="font-medium text-gray-900 mb-3">Information to Display</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showGallery"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showGallery !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showGallery: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showGallery" className="text-sm font-medium text-gray-700">Image Gallery</label>
                  <p className="text-xs text-gray-500 mt-1">Show multiple product images</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showPopupDescription"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showDescription !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showDescription: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showPopupDescription" className="text-sm font-medium text-gray-700">Description</label>
                  <p className="text-xs text-gray-500 mt-1">Product description text</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showPopupPrice"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showPrice !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showPrice: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showPopupPrice" className="text-sm font-medium text-gray-700">Price Information</label>
                  <p className="text-xs text-gray-500 mt-1">Pricing details and options</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showPopupNote"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showNote !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showNote: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showPopupNote" className="text-sm font-medium text-gray-700">Product Note</label>
                  <p className="text-xs text-gray-500 mt-1">Additional notes or information</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showAttributes"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showAttributes !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showAttributes: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showAttributes" className="text-sm font-medium text-gray-700">Product Attributes</label>
                  <p className="text-xs text-gray-500 mt-1">Technical specifications and features</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t border-gray-200 pt-6">
            <h6 className="font-medium text-gray-900 mb-3">Action Buttons</h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showAddToCart"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showAddToCart !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showAddToCart: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showAddToCart" className="text-sm font-medium text-gray-700">Add to Cart Button</label>
                  <p className="text-xs text-gray-500 mt-1">Allow adding products to cart from popup</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="showCustomizeButton"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={content?.productPopupSettings?.showCustomizeButton !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showCustomizeButton: e.target.checked,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="showCustomizeButton" className="text-sm font-medium text-gray-700">Customize Button</label>
                  <p className="text-xs text-gray-500 mt-1">Button to customize or personalize product</p>
                </div>
              </div>
            </div>
          </div>

          {/* Popup Size */}
          <div className="border-t border-gray-200 pt-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Popup Size</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={content?.productPopupSettings?.popupSize || "medium"}
                onChange={(e) =>
                  updateContent("productPopupSettings", {
                    ...(content?.productPopupSettings || {}),
                    popupSize: e.target.value,
                  })
                }
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="fullscreen">Full Screen</option>
              </select>
              <p className="text-xs text-gray-500">Choose the size of the popup window</p>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
</div>

{/* Product Grid Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-emerald-100 p-2 rounded-lg mr-3">
      <Grid className="h-5 w-5 text-emerald-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Product Grid Configuration</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how products are arranged and displayed in the grid</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Responsive Columns */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Responsive Layout</h5>
      <p className="text-sm text-gray-600 mb-4">Set how many product columns to show on different screen sizes</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Mobile (phones)</label>
          <select
            value={content?.productGridColumnsResponsive?.mobile || 1}
            onChange={(e) =>
              updateContent("productGridColumnsResponsive", {
                ...(content?.productGridColumnsResponsive || {}),
                mobile: parseInt(e.target.value),
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value={1}>1 Column</option>
            <option value={2}>2 Columns</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Tablet</label>
          <select
            value={content?.productGridColumnsResponsive?.tablet || 2}
            onChange={(e) =>
              updateContent("productGridColumnsResponsive", {
                ...(content?.productGridColumnsResponsive || {}),
                tablet: parseInt(e.target.value),
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value={1}>1 Column</option>
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Desktop</label>
          <select
            value={content?.productGridColumnsResponsive?.desktop || 3}
            onChange={(e) =>
              updateContent("productGridColumnsResponsive", {
                ...(content?.productGridColumnsResponsive || {}),
                desktop: parseInt(e.target.value),
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
            <option value={4}>4 Columns</option>
          </select>
        </div>
      </div>
    </div>

    {/* Card Styling */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Card Appearance</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Product Card Style</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.productCardStyle || "shadow"}
            onChange={(e) => updateContent("productCardStyle", e.target.value)}
          >
            <option value="shadow">Shadow Effect</option>
            <option value="outline">Outline Border</option>
            <option value="flat">Flat Design</option>
          </select>
          <p className="text-xs text-gray-500">Visual style for product cards</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="showQuickAction"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                checked={content?.showQuickAction !== false}
                onChange={(e) => updateContent("showQuickAction", e.target.checked)}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="showQuickAction" className="text-sm font-medium text-gray-700">Quick Action Button</label>
              <p className="text-xs text-gray-500 mt-1">Show floating action button on product cards</p>
            </div>
          </div>

          {content?.showQuickAction !== false && (
            <div className="pl-7 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Quick Action Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={content?.quickActionIcon || "customize"}
                onChange={(e) => updateContent("quickActionIcon", e.target.value)}
              >
                <option value="customize">Customize (Pencil Icon)</option>
                <option value="cart">Add to Cart (Cart Icon)</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Animation Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5">
          <input
            type="checkbox"
            id="fade-effect"
            checked={content?.fadeEffectEnabled !== false}
            onChange={(e) => updateContent("fadeEffectEnabled", e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="fade-effect" className="text-sm font-medium text-gray-700">Fade-in Animation</label>
          <p className="text-xs text-gray-500 mt-1">Products fade in smoothly as they load or come into view</p>
        </div>
      </div>
    </div>
  </div>
</div>

{/* Load More Settings */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-amber-100 p-2 rounded-lg mr-3">
      <RefreshCw className="h-5 w-5 text-amber-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Load More Settings</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how additional products are loaded and displayed</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Button Configuration */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Button Configuration</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Button Text</label>
          <input
            type="text"
            value={content?.loadMoreButtonText || "Load More"}
            onChange={(e) => updateContent("loadMoreButtonText", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Load More"
          />
          <p className="text-xs text-gray-500">Text displayed on the load more button</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Button Color</label>
          <div className="flex items-center space-x-3">
            <ColorPicker
              color={content?.loadMoreButtonColor || "#1e3a8a"}
              onChange={(color) => updateContent("loadMoreButtonColor", color)}
            />
            <div 
              className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
              style={{ backgroundColor: content?.loadMoreButtonColor || "#1e3a8a" }}
            />
          </div>
          <p className="text-xs text-gray-500">Background color for the load more button</p>
        </div>
      </div>
    </div>

    {/* Load Increment Settings */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Load Increment</h5>
      <p className="text-sm text-gray-600 mb-4">How many products to load each time the button is clicked</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Desktop Load Count</label>
          <div className="flex items-center space-x-3">
            <input
              type="number"
              min="1"
              max="24"
              value={content?.loadIncrementDesktop || 6}
              onChange={(e) => updateContent("loadIncrementDesktop", parseInt(e.target.value))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <span className="text-sm text-gray-500">products</span>
          </div>
          <p className="text-xs text-gray-500">Number of products to load on desktop devices</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Mobile Load Count</label>
          <div className="flex items-center space-x-3">
            <input
              type="number"
              min="1"
              max="12"
              value={content?.loadIncrementMobile || 3}
              onChange={(e) => updateContent("loadIncrementMobile", parseInt(e.target.value))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <span className="text-sm text-gray-500">products</span>
          </div>
          <p className="text-xs text-gray-500">Number of products to load on mobile devices</p>
        </div>
      </div>
    </div>

    {/* Loading Animation */}
    <div className="border-t border-gray-200 pt-6">
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="loading-animation"
              checked={content?.showLoadingAnimation !== false}
              onChange={(e) => updateContent("showLoadingAnimation", e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="loading-animation" className="text-sm font-medium text-gray-700">Show Loading Animation</label>
            <p className="text-xs text-gray-500 mt-1">Display animation while products are loading</p>
          </div>
        </div>

        {content?.showLoadingAnimation !== false && (
          <div className="pl-7 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Animation URL</label>
            <input
              type="text"
              value={content?.loadingAnimationUrl || "../images/loading.gif"}
              onChange={(e) => updateContent("loadingAnimationUrl", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="../images/loading.gif"
            />
            <ImageUploader
              value={content?.loadingAnimationUrl || "../images/loading.gif"}
              onChange={(url) => updateContent("loadingAnimationUrl", url)}
              className="mt-2"
            />  
            <p className="text-xs text-gray-500">URL or path to the loading animation image</p>
          </div>
        )}
      </div>
    </div>
  </div>
</div>

{/* API Settings */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-slate-100 p-2 rounded-lg mr-3">
      <Settings className="h-5 w-5 text-slate-600" />
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-gray-900">API Settings</h4>
      <p className="text-sm text-gray-500 mt-1">Configure API endpoints for data retrieval</p>
    </div>
    <div className="bg-blue-100 p-2 rounded-lg">
      <Info className="h-4 w-4 text-blue-600" />
    </div>
  </div>

  <div className="space-y-6">
    {/* Warning Notice */}
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">!</span>
          </div>
        </div>
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-1">Advanced Configuration</p>
          <p>These settings are for advanced users. Most users should leave these at their default values unless specifically instructed otherwise.</p>
        </div>
      </div>
    </div>

    {/* API Endpoints */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">API Endpoints</h5>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Categories API Path</label>
          <input
            type="text"
            value={content?.apiPath || "/api/categories/basic"}
            onChange={(e) => updateContent("apiPath", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-sm"
            placeholder="/api/categories/basic"
          />
          <p className="text-xs text-gray-500">API endpoint for fetching category data</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Products API Path</label>
          <input
            type="text"
            value={content?.productApiPath || "/api/products/basic"}
            onChange={(e) => updateContent("productApiPath", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-sm"
            placeholder="/api/products/basic"
          />
          <p className="text-xs text-gray-500">API endpoint for fetching product data</p>
        </div>
      </div>
    </div>

    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Current Configuration</h6>
          <p className="text-sm text-gray-500">Active API endpoints</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">2 Endpoints</div>
          <div className="text-xs text-gray-500">Categories & Products</div>
        </div>
      </div>
    </div>
  </div>
</div>





    
    </div>
  );
};

export default ProductCatalogVerticalForm;







// // src/components/admin/page-management/section-forms/ProductCatalogVerticalForm.js
// import React from "react";
// import { Info } from "lucide-react";
// import ColorPicker from "../../shared/ColorPicker";
// import { useEffect, useState } from "react";
// import axios from "axios";

// const ProductCatalogVerticalForm = ({ content, updateContent }) => {
//   const [availableCategories, setAvailableCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [availableParentCategories, setAvailableParentCategories] = useState(
//     []
//   );
//   const [loadingParentCategories, setLoadingParentCategories] = useState(false);
//   const [loadedParentCategories, setLoadedParentCategories] = useState([]);

//   // useEffect to fetch categories
//   useEffect(() => {
//     const fetchCategories = async () => {
//       if (content?.showAllCategories === false) {
//         try {
//           setLoadingCategories(true);
//           const response = await axios.get("/api/categories/basic");
//           setAvailableCategories(response.data);

//           // Initialize selected categories if not already set
//           if (!content.selectedCategoryIds) {
//             const initialSelected = response.data.map((cat) => cat._id);
//             updateContent("selectedCategoryIds", initialSelected);
//           }
//         } catch (error) {
//           console.error("Error fetching categories:", error);
//         } finally {
//           setLoadingCategories(false);
//         }
//       }
//     };

//     fetchCategories();
//   }, [content?.showAllCategories]);

//   //useEffect to update the category selection UI
//   useEffect(() => {
//     const container = document.getElementById("category-selection-container");
//     if (
//       container &&
//       availableCategories.length > 0 &&
//       content?.showAllCategories === false
//     ) {
//       container.innerHTML = "";

//       availableCategories.forEach((category) => {
//         const isSelected =
//           content.selectedCategoryIds?.includes(category._id) || false;

//         const div = document.createElement("div");
//         div.className = "flex items-center mb-2";

//         const input = document.createElement("input");
//         input.type = "checkbox";
//         input.className = "mr-2";
//         input.checked = isSelected;
//         input.onchange = () => {
//           const currentSelected = [...(content.selectedCategoryIds || [])];
//           if (input.checked && !currentSelected.includes(category._id)) {
//             updateContent("selectedCategoryIds", [
//               ...currentSelected,
//               category._id,
//             ]);
//           } else if (!input.checked && currentSelected.includes(category._id)) {
//             updateContent(
//               "selectedCategoryIds",
//               currentSelected.filter((id) => id !== category._id)
//             );
//           }
//         };

//         const label = document.createElement("label");
//         label.className = "text-sm";
//         label.textContent = category.name;

//         div.appendChild(input);
//         div.appendChild(label);
//         container.appendChild(div);
//       });
//     }
//   }, [
//     availableCategories,
//     content?.selectedCategoryIds,
//     content?.showAllCategories,
//   ]);

//   useEffect(() => {
//     const fetchParentCategories = async () => {
//       if (content?.showParentCategories === true) {
//         try {
//           setLoadingParentCategories(true);
//           const response = await axios.get("/api/parent-categories/basic");
//           setAvailableParentCategories(response.data);
//           setLoadedParentCategories(response.data);

//           // Initialize selected parent categories if not already set
//           if (
//             !content.selectedParentCategoryIds ||
//             content.selectedParentCategoryIds.length === 0
//           ) {
//             const initialSelected = response.data.map((parent) => parent._id);
//             updateContent("selectedParentCategoryIds", initialSelected);
//           }
//         } catch (error) {
//           console.error("Error fetching parent categories:", error);
//         } finally {
//           setLoadingParentCategories(false);
//         }
//       }
//     };

//     fetchParentCategories();
//   }, [content?.showParentCategories]);

//   // Helper for handling color changes to ensure proper format
//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith("#") && value.length > 0) {
//       value = "#" + value;
//     }
//     updateContent(key, value);
//   };

//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Categories Section</h3>

//       {/* Basic Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Basic Settings</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Section Title
//           </label>
//           <input
//             type="text"
//             value={content?.title || "Browse Categories"}
//             onChange={(e) => updateContent("title", e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={
//               content?.description ||
//               "Browse our selection of premium packaging solutions"
//             }
//             onChange={(e) => updateContent("description", e.target.value)}
//             placeholder="Brief description of the category section"
//           ></textarea>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Background Color
//           </label>
//           <ColorPicker
//             color={content?.backgroundColor || "#F9FAFB"}
//             onChange={(color) => updateContent("backgroundColor", color)}
//           />
//         </div>

//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.searchEnabled !== false}
//               onChange={(e) => updateContent("searchEnabled", e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Search</span>
//           </label>
//         </div>

//         {content?.searchEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Search Placeholder
//             </label>
//             <input
//               type="text"
//               value={content?.searchPlaceholder || "Search categories..."}
//               onChange={(e) =>
//                 updateContent("searchPlaceholder", e.target.value)
//               }
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>

//       {/* Layout Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Layout Settings</h4>

//         {/* <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Layout Type</label>
//           <select
//             value={content?.layoutType || "vertical"}
//             onChange={(e) => updateContent("layoutType", e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="vertical">
//               Vertical (Categories on left, Products on right)
//             </option>
//             <option value="slider">Horizontal Slider</option>
//           </select>
//         </div> */}

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Slider Threshold
//           </label>
//           <div className="flex items-center">
//             <input
//               type="number"
//               min="1"
//               value={content?.sliderThreshold || 9}
//               onChange={(e) =>
//                 updateContent("sliderThreshold", parseInt(e.target.value))
//               }
//               className="w-24 p-2 border rounded mr-2"
//             />
//             <span className="text-sm text-gray-600">
//               Switch to slider if more categories than this number
//             </span>
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Category Image Shape
//           </label>
//           <select
//             value={content?.categoryImageShape || "circle"}
//             onChange={(e) =>
//               updateContent("categoryImageShape", e.target.value)
//             }
//             className="w-full p-2 border rounded"
//           >
//             <option value="circle">Circle</option>
//             <option value="square">Square with Rounded Corners</option>
//           </select>
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Category Border Color
//             </label>
//             <ColorPicker
//               color={content?.categoryBorderColor || "#3b82f6"}
//               onChange={(color) => updateContent("categoryBorderColor", color)}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Category Background Color
//             </label>
//             <ColorPicker
//               color={content?.categoryBackgroundColor || "#f3f4f6"} // Default gray-100
//               onChange={(color) =>
//                 updateContent("categoryBackgroundColor", color)
//               }
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Category Text Color
//             </label>
//             <ColorPicker
//               color={content?.categoryTextColor || "#1f2937"} // Default gray-800
//               onChange={(color) => updateContent("categoryTextColor", color)}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Selected Category Border Color
//             </label>
//             <ColorPicker
//               color={content?.selectedCategoryBorderColor || "#3b82f6"}
//               onChange={(color) =>
//                 updateContent("selectedCategoryBorderColor", color)
//               }
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Selected Category Background
//             </label>
//             <ColorPicker
//               color={content?.selectedCategoryBackgroundColor || "#60a5fa"} // Default blue-400
//               onChange={(color) =>
//                 updateContent("selectedCategoryBackgroundColor", color)
//               }
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Selected Category Text
//             </label>
//             <ColorPicker
//               color={content?.selectedCategoryTextColor || "#ffffff"} // Default white
//               onChange={(color) =>
//                 updateContent("selectedCategoryTextColor", color)
//               }
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Category Hover Background
//             </label>
//             <ColorPicker
//               color={content?.categoryHoverBackgroundColor || "#e5e7eb"} // Default gray-200
//               onChange={(color) =>
//                 updateContent("categoryHoverBackgroundColor", color)
//               }
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Category Hover Text
//             </label>
//             <ColorPicker
//               color={content?.categoryHoverTextColor || "#111827"} // Default gray-900
//               onChange={(color) =>
//                 updateContent("categoryHoverTextColor", color)
//               }
//             />
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Vertical Layout Image Size
//           </label>
//           <select
//             value={content?.verticalImageSize || "medium"}
//             onChange={(e) => updateContent("verticalImageSize", e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="small">Small</option>
//             <option value="medium">Medium</option>
//             <option value="large">Large</option>
//           </select>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Slider Layout Image Size
//           </label>
//           <select
//             value={content?.sliderImageSize || "medium"}
//             onChange={(e) => updateContent("sliderImageSize", e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="small">Small</option>
//             <option value="medium">Medium</option>
//             <option value="large">Large</option>
//           </select>
//         </div>
//       </div>

//       {/* Category Selection */}
//       <div className="bg-gray-50 p-4 rounded-md mb-6">
//         <h4 className="font-medium mb-4">Category Selection</h4>

//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showAllCategories !== false}
//               onChange={(e) =>
//                 updateContent("showAllCategories", e.target.checked)
//               }
//               className="mr-2"
//             />
//             <span className="text-sm font-medium">Show All Categories</span>
//           </label>
//         </div>

//         {content?.showAllCategories === false && (
//           <div className="mt-4">
//             <label className="block text-sm font-medium mb-2">
//               Select Specific Categories
//             </label>
//             <div className="max-h-60 overflow-y-auto border rounded p-2">
//               {/* This will be filled in the component */}
//               <div className="text-sm text-gray-500 mb-2">
//                 Loading categories... If no categories appear, save the page
//                 first and edit again.
//               </div>
//               <div id="category-selection-container"></div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Parent Category Display Option */}
//       <div className="bg-gray-50 p-4 rounded-md mb-6">
//         <h4 className="font-medium mb-4">Category Display Options</h4>

//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showParentCategories === true}
//               onChange={(e) =>
//                 updateContent("showParentCategories", e.target.checked)
//               }
//               className="mr-2"
//             />
//             <span className="text-sm font-medium">Show Parent Categories</span>
//           </label>
//           <p className="text-xs text-gray-500 mt-1 ml-6">
//             When enabled, shows parent categories with related child categories
//             in rows
//           </p>
//         </div>

//         {content?.showParentCategories === true && (
//           <div className="mb-4 mt-4">
//             <label className="flex items-center">
//               <input
//                 type="checkbox"
//                 checked={content?.showAllParentCategories !== false}
//                 onChange={(e) =>
//                   updateContent("showAllParentCategories", e.target.checked)
//                 }
//                 className="mr-2"
//               />
//               <span className="text-sm font-medium">
//                 Show All Parent Categories
//               </span>
//             </label>
//             <p className="text-xs text-gray-500 mt-1 ml-6">
//               When disabled, you can select specific parent categories to
//               display
//             </p>
//           </div>
//         )}

//         {content?.showParentCategories === true &&
//           content?.showAllParentCategories === false && (
//             <div className="mt-4">
//               <label className="block text-sm font-medium mb-2">
//                 Select Specific Parent Categories
//               </label>
//               <div className="max-h-60 overflow-y-auto border rounded p-2">
//                 {loadingParentCategories ? (
//                   <div className="text-sm text-gray-500 mb-2">
//                     Loading parent categories...
//                   </div>
//                 ) : loadedParentCategories.length > 0 ? (
//                   loadedParentCategories.map((parentCategory) => (
//                     <div
//                       key={parentCategory._id}
//                       className="flex items-center mb-2"
//                     >
//                       <input
//                         type="checkbox"
//                         className="mr-2"
//                         checked={
//                           content.selectedParentCategoryIds?.includes(
//                             parentCategory._id
//                           ) || false
//                         }
//                         onChange={(e) => {
//                           const currentSelected = [
//                             ...(content.selectedParentCategoryIds || []),
//                           ];
//                           if (
//                             e.target.checked &&
//                             !currentSelected.includes(parentCategory._id)
//                           ) {
//                             updateContent("selectedParentCategoryIds", [
//                               ...currentSelected,
//                               parentCategory._id,
//                             ]);
//                           } else if (
//                             !e.target.checked &&
//                             currentSelected.includes(parentCategory._id)
//                           ) {
//                             updateContent(
//                               "selectedParentCategoryIds",
//                               currentSelected.filter(
//                                 (id) => id !== parentCategory._id
//                               )
//                             );
//                           }
//                         }}
//                       />
//                       <label className="text-sm">{parentCategory.name}</label>
//                     </div>
//                   ))
//                 ) : (
//                   <div className="text-sm text-gray-500">
//                     No parent categories found. Try saving the page first and
//                     edit again.
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//         {content?.showParentCategories === true && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Parent Category Display Style
//             </label>
//             <select
//               value={content?.parentCategoryDisplayStyle || "nameAndImage"}
//               onChange={(e) =>
//                 updateContent("parentCategoryDisplayStyle", e.target.value)
//               }
//               className="w-full p-2 border rounded"
//             >
//               <option value="nameAndImage">Name with Image</option>
//               <option value="nameOnly">Name Only</option>
//             </select>
//           </div>
//         )}

//         {content?.showParentCategories === true && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Child Category Image Size
//             </label>
//             <select
//               value={content?.childCategoryImageSize || "small"}
//               onChange={(e) =>
//                 updateContent("childCategoryImageSize", e.target.value)
//               }
//               className="w-full p-2 border rounded"
//             >
//               <option value="tiny">Tiny</option>
//               <option value="small">Small</option>
//               <option value="medium">Medium</option>
//             </select>
//           </div>
//         )}
//       </div>

//       {/* Price Display Configuration */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Price Display Configuration</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Display Price Type
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.priceDisplayType || "regular"}
//             onChange={(e) => updateContent("priceDisplayType", e.target.value)}
//           >
//             <option value="regular">Regular Price</option>
//             <option value="tier">Pricing Tier (Last Bound)</option>
//             <option value="wholesale">Wholesale Price</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Select which price to display for products when "Show Price" is
//             enabled
//           </p>
//         </div>

//         {/* Quantity Selection for Add to Cart */}
//         <div className="mt-6">
//           <div className="flex items-center mb-4">
//             <input
//               type="checkbox"
//               id="allowQuantitySelection"
//               className="mr-2"
//               checked={content?.allowQuantitySelection === true}
//               onChange={(e) =>
//                 updateContent("allowQuantitySelection", e.target.checked)
//               }
//             />
//             <label
//               htmlFor="allowQuantitySelection"
//               className="text-sm font-medium"
//             >
//               Allow Quantity Selection when Adding to Cart
//             </label>
//           </div>

//           {content?.allowQuantitySelection && (
//             <div className="mb-4 ml-6">
//               <label className="block text-sm font-medium mb-1">
//                 Default Quantity
//               </label>
//               <input
//                 type="number"
//                 className="w-full p-2 border rounded"
//                 min="1"
//                 value={
//                   content?.defaultQuantity ||
//                   (content?.priceDisplayType === "wholesale" ? 50 : 1)
//                 }
//                 onChange={(e) =>
//                   updateContent(
//                     "defaultQuantity",
//                     parseInt(e.target.value, 10) ||
//                       (content?.priceDisplayType === "wholesale" ? 50 : 1)
//                   )
//                 }
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 {content?.priceDisplayType === "regular" &&
//                   "For regular price, quantity can be incremented by 1 (default is 1)"}
//                 {content?.priceDisplayType === "tier" &&
//                   "For tier pricing, quantity will follow tier bounds"}
//                 {content?.priceDisplayType === "wholesale" &&
//                   "For wholesale price, quantity can be incremented by 1 (default is 50)"}
//               </p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* User Visibility Configuration */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Price and Cart Visibility</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Show Price For
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.priceVisibility || "all"}
//             onChange={(e) => updateContent("priceVisibility", e.target.value)}
//           >
//             <option value="all">Everyone (Default)</option>
//             <option value="authenticated">Logged In Users Only</option>
//             <option value="business">Business Users Only</option>
//             <option value="regular">Regular Users Only</option>
//             <option value="none">No One (Hide Prices)</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Control which types of users can see product pricing
//           </p>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Add to Cart For
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.cartVisibility || "all"}
//             onChange={(e) => updateContent("cartVisibility", e.target.value)}
//           >
//             <option value="all">Everyone (Default)</option>
//             <option value="authenticated">Logged In Users Only</option>
//             <option value="business">Business Users Only</option>
//             <option value="regular">Regular Users Only</option>
//             <option value="none">No One (Disable Add to Cart)</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Control which types of users can add products to cart
//           </p>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Login Prompt Message
//           </label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             placeholder="Please login to see prices and purchase products"
//             value={
//               content?.loginPromptMessage ||
//               "Please login to see prices and purchase products"
//             }
//             onChange={(e) =>
//               updateContent("loginPromptMessage", e.target.value)
//             }
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             Message to show when user doesn't have permission to see prices or
//             add to cart
//           </p>
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Information Display</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Information Fields
//           </label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) =>
//                   updateContent("productDisplayFields", {
//                     ...(content?.productDisplayFields || {}),
//                     showName: e.target.checked,
//                   })
//                 }
//               />
//               <label htmlFor="showName" className="text-sm">
//                 Show Product Name
//               </label>
//             </div>

//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) =>
//                   updateContent("productDisplayFields", {
//                     ...(content?.productDisplayFields || {}),
//                     showPrice: e.target.checked,
//                   })
//                 }
//               />
//               <label htmlFor="showPrice" className="text-sm">
//                 Show Price
//               </label>
//             </div>

//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={
//                   content?.productDisplayFields?.showDescription === true
//                 }
//                 onChange={(e) =>
//                   updateContent("productDisplayFields", {
//                     ...(content?.productDisplayFields || {}),
//                     showDescription: e.target.checked,
//                   })
//                 }
//               />
//               <label htmlFor="showDescription" className="text-sm">
//                 Show Description
//               </label>
//             </div>

//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) =>
//                   updateContent("productDisplayFields", {
//                     ...(content?.productDisplayFields || {}),
//                     showNote: e.target.checked,
//                   })
//                 }
//               />
//               <label htmlFor="showNote" className="text-sm">
//                 Show Note
//               </label>
//             </div>
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Product Info Position
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || "below"}
//             onChange={(e) =>
//               updateContent("productInfoPosition", e.target.value)
//             }
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Text Alignment
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || "left"}
//             onChange={(e) =>
//               updateContent("productTextAlignment", e.target.value)
//             }
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Product Name Max Lines
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) =>
//               updateContent("productNameLines", parseInt(e.target.value, 10))
//             }
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>

//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Description Max Lines
//             </label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) =>
//                 updateContent(
//                   "productDescriptionLines",
//                   parseInt(e.target.value, 10)
//                 )
//               }
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Image Aspect Ratio
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || "square"}
//             onChange={(e) =>
//               updateContent("productImageAspectRatio", e.target.value)
//             }
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Click Behavior</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             When a product is clicked:
//           </label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== "popup"}
//                 onChange={() =>
//                   updateContent("productClickBehavior", "navigate")
//                 }
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">
//                 Navigate to product detail page
//               </label>
//             </div>

//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === "popup"}
//                 onChange={() => updateContent("productClickBehavior", "popup")}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">
//                 Show product details in popup
//               </label>
//             </div>
//           </div>
//         </div>

//         {content?.productClickBehavior === "popup" && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>

//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">
//                 Information to Display
//               </label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={
//                       content?.productPopupSettings?.showGallery !== false
//                     }
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showGallery: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showGallery" className="text-sm">
//                     Image Gallery
//                   </label>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPopupDescription"
//                     className="mr-2"
//                     checked={
//                       content?.productPopupSettings?.showDescription !== false
//                     }
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showDescription: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showPopupDescription" className="text-sm">
//                     Description
//                   </label>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPopupPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showPrice: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showPopupPrice" className="text-sm">
//                     Price Information
//                   </label>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPopupNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showNote: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showPopupNote" className="text-sm">
//                     Product Note
//                   </label>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={
//                       content?.productPopupSettings?.showAttributes !== false
//                     }
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showAttributes: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">
//                     Product Attributes
//                   </label>
//                 </div>
//               </div>
//             </div>

//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">
//                 Action Buttons
//               </label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={
//                       content?.productPopupSettings?.showAddToCart !== false
//                     }
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showAddToCart: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">
//                     Add to Cart Button
//                   </label>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={
//                       content?.productPopupSettings?.showCustomizeButton !==
//                       false
//                     }
//                     onChange={(e) =>
//                       updateContent("productPopupSettings", {
//                         ...(content?.productPopupSettings || {}),
//                         showCustomizeButton: e.target.checked,
//                       })
//                     }
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">
//                     Customize Product Button
//                   </label>
//                 </div>
//               </div>
//             </div>

//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">
//                 Popup Size
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || "medium"}
//                 onChange={(e) =>
//                   updateContent("productPopupSettings", {
//                     ...(content?.productPopupSettings || {}),
//                     popupSize: e.target.value,
//                   })
//                 }
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Product Grid Configuration */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Grid Configuration</h4>

//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Mobile Columns
//             </label>
//             <select
//               value={content?.productGridColumnsResponsive?.mobile || 1}
//               onChange={(e) =>
//                 updateContent("productGridColumnsResponsive", {
//                   ...(content?.productGridColumnsResponsive || {}),
//                   mobile: parseInt(e.target.value),
//                 })
//               }
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Tablet Columns
//             </label>
//             <select
//               value={content?.productGridColumnsResponsive?.tablet || 2}
//               onChange={(e) =>
//                 updateContent("productGridColumnsResponsive", {
//                   ...(content?.productGridColumnsResponsive || {}),
//                   tablet: parseInt(e.target.value),
//                 })
//               }
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Desktop Columns
//             </label>
//             <select
//               value={content?.productGridColumnsResponsive?.desktop || 3}
//               onChange={(e) =>
//                 updateContent("productGridColumnsResponsive", {
//                   ...(content?.productGridColumnsResponsive || {}),
//                   desktop: parseInt(e.target.value),
//                 })
//               }
//               className="w-full p-2 border rounded"
//             >
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//             </select>
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Product Card Style
//           </label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || "shadow"}
//             onChange={(e) => updateContent("productCardStyle", e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>

//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent("showQuickAction", e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">
//             Show Quick Action Button
//           </label>
//         </div>

//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Quick Action Icon
//             </label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || "customize"}
//               onChange={(e) => updateContent("quickActionIcon", e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}

//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.fadeEffectEnabled !== false}
//               onChange={(e) =>
//                 updateContent("fadeEffectEnabled", e.target.checked)
//               }
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Fade-in Animation</span>
//           </label>
//         </div>
//       </div>

//       {/* Load More Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Load More Settings</h4>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Load More Button Text
//           </label>
//           <input
//             type="text"
//             value={content?.loadMoreButtonText || "Load More"}
//             onChange={(e) =>
//               updateContent("loadMoreButtonText", e.target.value)
//             }
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Load More Button Color
//           </label>
//           <ColorPicker
//             color={content?.loadMoreButtonColor || "#1e3a8a"}
//             onChange={(color) => updateContent("loadMoreButtonColor", color)}
//           />
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Desktop Load Increment
//             </label>
//             <input
//               type="number"
//               min="1"
//               max="24"
//               value={content?.loadIncrementDesktop || 6}
//               onChange={(e) =>
//                 updateContent("loadIncrementDesktop", parseInt(e.target.value))
//               }
//               className="w-full p-2 border rounded"
//             />
//             <p className="text-xs text-gray-500 mt-1">
//               Number of products to load each time on desktop
//             </p>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">
//               Mobile Load Increment
//             </label>
//             <input
//               type="number"
//               min="1"
//               max="12"
//               value={content?.loadIncrementMobile || 3}
//               onChange={(e) =>
//                 updateContent("loadIncrementMobile", parseInt(e.target.value))
//               }
//               className="w-full p-2 border rounded"
//             />
//             <p className="text-xs text-gray-500 mt-1">
//               Number of products to load each time on mobile
//             </p>
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showLoadingAnimation !== false}
//               onChange={(e) =>
//                 updateContent("showLoadingAnimation", e.target.checked)
//               }
//               className="mr-2"
//             />
//             <span className="text-sm">Show Loading Animation</span>
//           </label>
//         </div>

//         {content?.showLoadingAnimation !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">
//               Loading Animation URL
//             </label>
//             <input
//               type="text"
//               value={content?.loadingAnimationUrl || "../images/loading.gif"}
//               onChange={(e) =>
//                 updateContent("loadingAnimationUrl", e.target.value)
//               }
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>

//       {/* API Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">API Settings</h4>
//           <div className="text-blue-500">
//             <Info size={16} />
//           </div>
//         </div>

//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these
//           at their default values.
//         </p>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Categories API Path
//           </label>
//           <input
//             type="text"
//             value={content?.apiPath || "/api/categories/basic"}
//             onChange={(e) => updateContent("apiPath", e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">
//             Products API Path
//           </label>
//           <input
//             type="text"
//             value={content?.productApiPath || "/api/products/basic"}
//             onChange={(e) => updateContent("productApiPath", e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogVerticalForm;
